import React, { useCallback, useEffect, useRef, useState } from "react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { OrderStatus } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { addMinutes, format, startOfDay, subDays } from "date-fns";

import { api } from "~/lib/api";
import { cn } from "~/lib/utils";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { useOrderStore } from "~/stores/use-order-store";
import ClockIcon from "../icons/clock-icon";
import DeliveryIcon from "../icons/delivery-icon";
import IdealIcon from "../icons/ideal-icon";
import VrodexIcon from "../icons/vrodex-icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { RegisterButton } from "../ui/register-button";
import OnlineOrderTimeSwiper from "./online-order-time-swiper";

export default function OnlineOrderDialog() {
  const utils = api.useUtils();
  const {
    currentOnlineOrder,
    setCurrentOnlineOrder,
    removeFromCurrentOnlineOrderQueue,
  } = useOrderStore();

  const [estimatedTime, setEstimatedTime] = useState<Date | undefined>();
  const { selectedDate } = useTempBookingStore();

  const { mutate: updateStatus } = api.order.updateOrderStatus.useMutation({
    onMutate: async ({ id, status }) => {
      await utils.order.getOnlineOrders.cancel();

      const prevAll = utils.order.getOnlineOrders.getData();

      utils.order.getOnlineOrders.setData(
        { startDate: startOfDay(subDays(selectedDate, 7)) },
        (old) => {
          if (!old) return prevAll;

          const previousOrder = old.find((order) => order.id === id);

          if (!previousOrder) {
            return [...old];
          }

          return [
            ...old.filter((order) => order.id !== previousOrder.id),
            {
              ...previousOrder,
              orderStatus: status,
              estimatedTime,
            },
          ];
        },
      );
    },
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef(new Audio("./audio/new_order_1.mp3"));

  const playSound = () => {
    // Reset to beginning if it's already playing
    audioRef.current.currentTime = 0;

    audioRef.current.play().catch((error) => {
      console.error("Failed to play sound:", error);
    });
  };

  const startRepeatingSound = useCallback(() => {
    if (intervalRef.current) return; // Already playing

    // Play immediately
    playSound();

    // Set up interval for repeating
    intervalRef.current = setInterval(() => {
      playSound();
    }, 7500);

    setIsPlaying(true);
  }, []);

  const stopRepeatingSound = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setIsPlaying(false);

      // Stop current playback
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  useEffect(() => {
    if (currentOnlineOrder?.orderStatus === OrderStatus.new) {
      startRepeatingSound();
    } else {
      stopRepeatingSound();
    }

    // Cleanup on unmount
    return () => {
      stopRepeatingSound();
    };
  }, [currentOnlineOrder, startRepeatingSound]);

  const handleClose = () => {
    if (currentOnlineOrder) {
      removeFromCurrentOnlineOrderQueue(currentOnlineOrder);
    }
    setEstimatedTime(undefined);
    setCurrentOnlineOrder(undefined);
  };

  return (
    <Dialog
      open={!!currentOnlineOrder}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent className="max-w-5xl w-full">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle></DialogTitle>
            <DialogDescription></DialogDescription>
          </VisuallyHidden>
        </DialogHeader>

        {currentOnlineOrder && (
          <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar flex flex-col gap-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="rounded-lg bg-background-tertiary p-4 justify-center items-center flex flex-col gap-4 relative">
                <span>Provider</span>
                <VrodexIcon className="h-12" />
                <span>Eigen website</span>
              </div>
              <div className="rounded-lg bg-background-tertiary p-4 justify-center items-center flex flex-col gap-4 relative">
                <span>Order type</span>
                <DeliveryIcon className="h-14 fill-white" />
                <span>{currentOnlineOrder.type}</span>
              </div>
              <div className="rounded-lg bg-background-tertiary p-4 justify-center items-center flex flex-col gap-4 relative">
                <span>Time</span>
                <ClockIcon className="w-12 h-12" />
                {!currentOnlineOrder.requestedTime && <span>ASAP</span>}
                {currentOnlineOrder.requestedTime && (
                  <span>
                    {format(currentOnlineOrder.requestedTime, "HH:mm")}
                  </span>
                )}
              </div>
              <div className="rounded-lg bg-background-tertiary p-4 justify-center items-center flex flex-col gap-4 relative">
                <span>Payment Method</span>
                <IdealIcon className="w-12 h-12" />
                <span>{currentOnlineOrder.paymentMethodName}</span>
              </div>
            </div>

            <div className="bg-background-tertiary rounded-lg p-4 flex flex-col">
              <span>{currentOnlineOrder.firstName}</span>
            </div>

            <div className="bg-background-tertiary rounded-lg p-4 flex flex-col">
              <span>
                {currentOnlineOrder.items.map((item) => (
                  <div
                    className="flex flex-row gap-4 justify-between items-center"
                    key={item.reference}
                  >
                    <div className="flex flex-row gap-2">
                      <span
                        className={cn(item.quantity > 1 && "font-bold")}
                      >{`${item.quantity}x`}</span>
                      <span>{item.name}</span>
                    </div>
                    <div>
                      <span>
                        {formatIntToCurrency(item.value * item.quantity)}
                      </span>
                    </div>
                  </div>
                ))}
              </span>
            </div>
            {currentOnlineOrder.orderStatus === OrderStatus.new && (
              <>
                <OnlineOrderTimeSwiper
                  onSelect={(minutes) => {
                    if (currentOnlineOrder.requestedTime) {
                      setEstimatedTime(
                        addMinutes(currentOnlineOrder.requestedTime, minutes),
                      );
                    } else {
                      setEstimatedTime(addMinutes(new Date(), minutes));
                    }
                  }}
                />
                <RegisterButton
                  variant="success"
                  className="text-xl"
                  disabled={!estimatedTime}
                  onClick={() => {
                    const newStatus =
                      currentOnlineOrder.orderStatus === OrderStatus.new
                        ? OrderStatus.accepted
                        : currentOnlineOrder.orderStatus ===
                            OrderStatus.accepted
                          ? OrderStatus.in_progress
                          : OrderStatus.in_delivery;
                    updateStatus({
                      id: currentOnlineOrder.id,
                      status: newStatus,
                      estimatedTime,
                    });
                    handleClose();
                  }}
                >
                  Accept
                </RegisterButton>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
